'use client';

import { useQuery } from '@tanstack/react-query';
import { countUserFlagged, countUserFlaggedNeedingAttention } from '@/lib/csam-user-flagged';
import {
  fetchContentModerationFindingsPage,
  provenanceKeys,
} from '@/shared/cors-provenance-api';
import { isAllCasesSelection } from '@/shared/case-summary';
import { useProvenanceSession } from './useProvenanceSession';
import { useContentModerationFolderName } from './useContentModerationFolderName';

/**
 * Count of user-flagged findings still waiting on a CSAM Verifier
 * (MANUAL + CONFIRMED + no reviewedBy). Pages through CONFIRMED findings
 * because the summary counters do not split creation method.
 */
export function useUserFlaggedAttentionCount(caseId: string | null | undefined) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  // Wire ``case_id`` is the folder segment, not the nav display name.
  const folderName = useContentModerationFolderName(caseId);
  const enabled = sessionEnabled && Boolean(folderName) && !isAllCasesSelection(caseId);

  return useQuery({
    queryKey: [
      ...provenanceKeys.contentModeration(),
      'user-flagged',
      'attention',
      cellId,
      folderName,
    ],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async ({ signal }) => {
      let nextToken: string | null = null;
      let count = 0;
      do {
        const page = await fetchContentModerationFindingsPage({
          cellId: cellId as string,
          accessToken: accessToken as string,
          caseId: folderName as string,
          detectionType: 'CSAM',
          status: 'CONFIRMED',
          limit: 100,
          nextToken,
          signal,
        });
        count += countUserFlaggedNeedingAttention(page.items);
        nextToken = page.nextToken;
      } while (nextToken);
      return count;
    },
  });
}

/**
 * Count of every MANUAL finding for the case, any status. The summary
 * counters do not split creation method, so this pages the findings list.
 */
export function useUserFlaggedCount(caseId: string | null | undefined) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  // Wire ``case_id`` is the folder segment, not the nav display name.
  const folderName = useContentModerationFolderName(caseId);
  const enabled = sessionEnabled && Boolean(folderName) && !isAllCasesSelection(caseId);

  return useQuery({
    queryKey: [
      ...provenanceKeys.contentModeration(),
      'user-flagged',
      'count',
      cellId,
      folderName,
    ],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async ({ signal }) => {
      let nextToken: string | null = null;
      let count = 0;
      do {
        const page = await fetchContentModerationFindingsPage({
          cellId: cellId as string,
          accessToken: accessToken as string,
          caseId: folderName as string,
          detectionType: 'CSAM',
          limit: 100,
          nextToken,
          signal,
        });
        count += countUserFlagged(page.items);
        nextToken = page.nextToken;
      } while (nextToken);
      return count;
    },
  });
}
